
import React, { useState } from 'react';
import Header from '@/components/layout/Header';
import Layout from '@/components/layout/Layout';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { FileText, Download, Shield, Scale } from 'lucide-react';
import { toast } from 'sonner';
import ContractTemplates from '@/components/legal/ContractTemplates';
import ContractPreviewDialog from '@/components/legal/ContractPreviewDialog';
import CopyrightGuides from '@/components/legal/CopyrightGuides';
import CopyrightFAQSection from '@/components/legal/CopyrightFAQSection';
import LegalAdvisorSection from '@/components/legal/LegalAdvisorSection';
import { contractTemplates, copyrightFAQs } from '@/components/legal/data';
import { Contract } from '@/components/legal/types';
import { generateContractContent } from '@/components/legal/contractUtils';

const LegalTools: React.FC = () => {
  const [activeTab, setActiveTab] = useState('contracts');
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<Contract | null>(null);
  
  const handleDownload = (template: Contract) => {
    // Create text content for the contract
    const content = generateContractContent(template);
    
    // Create a blob with the content
    const blob = new Blob([content], { type: 'text/plain' });
    
    // Create download link
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${template.title.replace(/\s+/g, '-').toLowerCase()}.txt`;
    
    // Trigger download
    document.body.appendChild(link);
    link.click();
    
    // Clean up
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    toast.success(`${template.title} has been downloaded`);
  };
  
  const handlePreview = (template: Contract) => {
    setSelectedTemplate(template);
    setPreviewDialogOpen(true);
  };
  
  const handleCopyright = () => {
    toast.success("Copyright registration guide added to your downloads");
  };

  return (
    <Layout>
      <div className="container max-w-7xl pt-8 pb-16">
        <div className="flex flex-col gap-2 mb-8">
          <h1 className="text-3xl font-bold tracking-tight">Legal Tools</h1>
          <p className="text-muted-foreground">
            Access essential legal resources designed for musicians and music professionals
          </p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <div className="flex justify-between items-center">
            <TabsList>
              <TabsTrigger value="contracts" className="flex items-center gap-2">
                <FileText className="h-4 w-4" />
                <span>Contracts</span>
              </TabsTrigger>
              <TabsTrigger value="copyright" className="flex items-center gap-2">
                <Shield className="h-4 w-4" />
                <span>Copyright</span>
              </TabsTrigger>
              <TabsTrigger value="advisor" className="flex items-center gap-2">
                <Scale className="h-4 w-4" />
                <span>Legal Advisor</span>
              </TabsTrigger>
            </TabsList>
            
            <Button variant="outline" className="hidden sm:flex items-center gap-2">
              <FileText className="h-4 w-4" />
              <span>Save to Library</span>
            </Button>
          </div>

          <TabsContent value="contracts" className="space-y-4">
            <ContractTemplates 
              templates={contractTemplates} 
              onPreview={handlePreview} 
              onDownload={handleDownload} 
            />
          </TabsContent>

          <TabsContent value="copyright" className="space-y-4">
            <CopyrightGuides onDownload={handleCopyright} />
            <CopyrightFAQSection faqs={copyrightFAQs} />
          </TabsContent>

          <TabsContent value="advisor" className="space-y-4">
            <LegalAdvisorSection />
          </TabsContent>
        </Tabs>
      </div>
      
      <ContractPreviewDialog 
        open={previewDialogOpen}
        onOpenChange={setPreviewDialogOpen}
        contract={selectedTemplate}
        onDownload={handleDownload}
      />
    </Layout>
  );
};

export default LegalTools;
